import jsPDF from "jspdf";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import medicGeekApi from "../../../config/axiosConfig";
import ProRichTextEditor from "../../../contexts/ProRichTextEditor";
import TipTap from "../../../styles/TipTap.css";
import {
  showErrorAlert,
  showInfoAlert,
  showSuccessAlert,
} from "./../../../components/alerts";

const CertMedicoList = ({
  setCurrentForm,
  currentForm,
  setSelectedCertMedicos,
}) => {
  const [certMedicos, setCertMedicos] = useState([]);
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [showForm] = useState(false);

  // Skeleton
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const certMedicosPerPage = 10;
  const filteredCertMedicos = certMedicos;
  const [pageCount, setPageCount] = useState(0);

  const [pacientes, setPacientes] = useState([]);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [selectedCertMedicosLocal, setSelectedCertMedicosLocal] =
    useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Variable para distinguir entre editar y crear

  const [formulario, setFormulario] = useState({
    fechaCertMedico: "",
    constatado: "",
    recomedacion: "",
  });
  const [sortColumn, setSortColumn] = useState(""); // Columna actual para ordenar
  const [sortDirection, setSortDirection] = useState("asc"); // Dirección del ordenamiento

  const [pageSize, setPageSize] = useState(10); // Inicialmente, muestra 10 registros por página
  const [currentPage, setCurrentPage] = useState(1);
  const [profile, setProfile] = useState({
    docNom: "",
    docEsp: "",
    docCed: "",
    docExe: "",
    docProv: "",
  });
  const [constatado, setConstatado] = useState("");
  const [recomedacion, setRecomedacion] = useState("");

  // Contador de caracteres
  // Contador de caracteres
  const maxChars1 = 5000;
  const [profileData, setProfileData] = useState(null);
  const searchInputRef = useRef(null);

  const openModal = (certMedico, customPageSize = 10) => {
    // Configura el certMedico seleccionado para la edición
    setSelectedCertMedicosLocal(certMedico);
    setPageSize(customPageSize); // Establece la cantidad de registros por página

    if (certMedico) {
      setIsEditing(true); // Establecer como modo edición cuando se abre el modal

      // Extrae los datos del paciente del certMedico
      const paciente = certMedico.paciente || {}; // Usa un objeto vacío si paciente no está definido

      setFormulario({
        fechaCertMedico: certMedico.certifi_fe || "",
        constatado: certMedico.constatado || "",
        recomedacion: certMedico.recomedacion || "",
        nombrePaciente: paciente.pcteNom || "",
        segundoNombrePaciente: paciente.pcteNom2 || "",
        apellidPaciente: paciente.pcteApe1 || "",
        segundoApellidPaciente: paciente.pcteApe2 || "",
      });

      // Establece el paciente seleccionado para el modal
      setSelectedPaciente(paciente);
    } else {
      setIsEditing(false); // Establecer como modo creación cuando se abre el modal para crear uno nuevo

      const now = new Date();
      const formattedDate = now.toLocaleString(); // Obtiene la fecha y hora actual en formato legible

      setFormulario({
        fechaCertMedico: formattedDate,
        constatado: "",
        recomedacion: "",
        estatusCertMedico: "activo",
        nombrePaciente: "",
        segundoNombrePaciente: "",
        apellidPaciente: "",
        segundoApellidPaciente: "",
      });

      setSelectedPaciente(null);
    }

    setModalOpen(true); // Abre el modal
  };

  const closeModal = () => {
    setSelectedCertMedicosLocal(null); // Limpia el CertMedico seleccionado
    setIsEditing(false); // Resetear el modo edición cuando se cierra el modal
    setModalOpen(false);
  };

  useEffect(() => {
    fetchProfile();
    fetchData();
  }, []);

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setPageNumber(0);
  };

  const handleModalSearchTermChange = (e) => {
    setModalSearchTerm(e.target.value);
    setModalCurrentPage(1);
  };

  const fetchData = async () => {
    setLoading(true); // Iniciar la carga
    try {
      const patientsResponse = await medicGeekApi.get(
        `/patients?search=${searchTerm}`,
        {
          params: {
            $or: [
              { pcteNom: { $regex: searchTerm, $options: "i" } },
              { pcteNom2: { $regex: searchTerm, $options: "i" } },
              { pcteApe1: { $regex: searchTerm, $options: "i" } },
            ],
          },
        }
      );
      const sortedPacientes = patientsResponse.data.sort(
        (a, b) => a.pcteId - b.pcteId
      );

      setPacientes(sortedPacientes);

      const certMedicosResponse = await medicGeekApi.get("/certmedicos");

      if (certMedicosResponse.status === 200) {
        const combinedData = certMedicosResponse.data.map((certMedico) => {
          const paciente = sortedPacientes.find(
            (p) => Number(p.pcteId) === Number(certMedico.pcteId)
          );

          return {
            ...certMedico,
            pcteNom: paciente?.pcteNom || "",
            pcteNom2: paciente?.pcteNom2 || "",
            pcteApe1: paciente?.pcteApe1 || "",
            pcteApe2: paciente?.pcteApe2 || "",
            pcteDir: paciente?.pcteDir || "",
            pcteCel: paciente?.pcteCel || "",
            paciente: paciente || {},
          };
        });

        const sortedData = combinedData.sort(
          (a, b) => a.certifiId - b.certifiId
        );
        setCertMedicos(sortedData);
        setPageCount(Math.ceil(certMedicos.length / itemsPerPage));
        setHasLoaded(true);
      } else {
        console.log(
          "Error al obtener datos de certificados médicos:",
          certMedicosResponse.statusText
        );
      }
    } catch (error) {
      console.log("Error al obtener datos:", error);
    } finally {
      setLoading(false); // Finalizar carga
    }
  };

  const fetchProfile = async () => {
    try {
      // Obtener el token desde localStorage
      const token = localStorage.getItem("token");
      if (token) {
        // Decodificar el token para obtener el nombre de usuario
        const decodedToken = jwtDecode(token);
        const username = decodedToken.username; // Asegúrate de que 'username' sea correcto

        if (username) {
          // Buscar perfil por nombre de usuario
          const response = await medicGeekApi.get(`/profile/user/${username}`);
          const data = response.data;

          // Verificar si se recibió el perfil
          if (data) {
            setProfileData(data);
            setIsEditing(true);
          } else {
            console.error("No se encontró el perfil del usuario.");
          }
        } else {
          console.error("No se pudo obtener el nombre de usuario del token.");
        }
      } else {
        console.error("No se encontró el token.");
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const handlePageChange = ({ selected }) => {
    setPageNumber(selected);
  };

  function matchesSensitiveSearch(fullName = "", searchTerm = "") {
    const search = searchTerm
      .toLowerCase()
      .trim();

    if (!search) {
      return true;
    }

    const searchWords = search
      .split(/\s+/)
      .filter(Boolean);

    const nameWords = fullName
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    const availableWords = [...nameWords];

    return searchWords.every((searchWord) => {
      const index = availableWords.findIndex((nameWord) =>
        nameWord.startsWith(searchWord)
      );

      if (index === -1) {
        return false;
      }

      availableWords.splice(index, 1);

      return true;
    });
  }

  // Filtro para los pacientes en funcion de termino de busqueda
 const filteredPacientes = pacientes.filter((paciente) => {
  const fullName = [
    paciente.pcteNom,
    paciente.pcteNom2,
    paciente.pcteApe1,
    paciente.pcteApe2,
  ]
    .filter(Boolean)
    .join(" ");

  return matchesSensitiveSearch(
    fullName,
    modalSearchTerm
  );
});

  // Filtrar y paginar certMedicos
 const displayedcertMedicos = [...certMedicos]
  .sort((a, b) => b.certifiId - a.certifiId)

  .filter((paciente) => {

    const fullName = [
      paciente.pcteNom,
      paciente.pcteNom2,
      paciente.pcteApe1,
      paciente.pcteApe2,
    ]
      .filter(Boolean)
      .join(" ");

    return matchesSensitiveSearch(
      fullName,
      searchTerm
    );
  })

  .slice(
    pageNumber * certMedicosPerPage,
    (pageNumber + 1) * certMedicosPerPage
  )

  .map((paciente) => ({
    ...paciente,

    fullName: [
      paciente.pcteNom,
      paciente.pcteNom2,
      paciente.pcteApe1,
      paciente.pcteApe2,
    ]
      .filter(Boolean)
      .join(" "),
  }));

  const guardarCertMedico = () => {
    const fechaActual = new Date();
    const dia = String(fechaActual.getDate()).padStart(2, "0");
    const mes = String(fechaActual.getMonth() + 1).padStart(2, "0");
    const año = fechaActual.getFullYear();
    const ampm = fechaActual.getHours() >= 12 ? "PM" : "AM";
    let horas = fechaActual.getHours() % 12;
    horas = horas === 0 ? 12 : horas;
    const minutos = String(fechaActual.getMinutes()).padStart(2, "0");

    const fechaHoraFormateada = `${dia}/${mes}/${año} ${horas}:${minutos} ${ampm}`;

    // Validación de campos
    if (!formulario.constatado.trim() || !formulario.recomedacion.trim()) {
      showErrorAlert(
        "Algunos campos son obligatorios. Por favor, complete la información."
      );
      return;
    }

    const nuevoCertMedico = {
      certifi_fe: fechaHoraFormateada,
      constatado: formulario.constatado.trim(),
      recomedacion: formulario.recomedacion.trim(),
      pcteId: selectedPaciente ? selectedPaciente.pcteId : null,
    };

    // Decide la acción según si es un registro nuevo o existente
    const action = selectedCertMedicosLocal ? "edit" : "create";
    const apiUrl =
      action === "edit"
        ? `/certmedicos/${selectedCertMedicosLocal.id}`
        : "/certmedicos";

    medicGeekApi[action === "edit" ? "put" : "post"](apiUrl, nuevoCertMedico)
      .then((response) => {
        closeModal();
        showSuccessAlert(
          "Certificado " +
            (action === "edit" ? "editado" : "creado") +
            " con éxito."
        );
        fetchData();
        // previsualizarPDF(nuevoCertMedico.certifi_fe);
      })
      .catch((error) => {
        showErrorAlert(
          "Error al " +
            (action === "edit" ? "editar" : "crear") +
            " el Certificado Médico."
        );
        console.error(
          "Error al " +
            (action === "edit" ? "editar" : "crear") +
            " el Certificado Médico",
          error
        );
      });
  };

  const editCertMedico = () => {
    if (selectedCertMedicosLocal && selectedCertMedicosLocal.certifiId) {
      const {
        certifiId,
        constatado: originalConstatado,
        recomedacion: originalRecomedacion,
      } = selectedCertMedicosLocal;

      const nuevoConstatado = formulario.constatado;
      const nuevaRecomedacion = formulario.recomedacion;

      const fechaActual = new Date();
      const dia = String(fechaActual.getDate()).padStart(2, "0");
      const mes = String(fechaActual.getMonth() + 1).padStart(2, "0"); // Meses empiezan en 0
      const año = fechaActual.getFullYear();
      const ampm = fechaActual.getHours() >= 12 ? "PM" : "AM";
      let horas = fechaActual.getHours() % 12;
      horas = horas === 0 ? 12 : horas; // Si es 0, establece 12
      const minutos = String(fechaActual.getMinutes()).padStart(2, "0");

      const fechaHoraFormateada = `${dia}/${mes}/${año} ${horas}:${minutos} ${ampm}`;

      // Verifica si ha habido cambios
      const hasChanges =
        originalConstatado !== nuevoConstatado ||
        originalRecomedacion !== nuevaRecomedacion;

      if (hasChanges) {
        const fechaActual = new Date().toISOString().split("T")[0];

        const newCertMedico = {
          certifi_fe: fechaHoraFormateada,
          constatado: nuevoConstatado,
          recomedacion: nuevaRecomedacion,
          pcteId: selectedPaciente ? selectedPaciente.pcteId : null,
        };

        // Realiza una solicitud POST para guardar el nuevo registro en el servidor
        medicGeekApi
          .post("/certmedicos", newCertMedico)
          .then((response) => {
            closeModal();
            showSuccessAlert("Nuevo Certificado creado con éxito.");
            fetchData();
          })
          .catch((error) => {
            console.error("Error al crear el nuevo Certificado Médico", error);
            showErrorAlert("Error al crear el nuevo Certificado Médico.");
          });
      } else {
        showInfoAlert("No se han detectado cambios.");
      }
    } else {
      showErrorAlert("No se puede editar: registro no válido.");
    }
  };

  const handlePacienteSelection = (paciente) => {
    setSelectedPaciente(paciente);
    setModalOpen(false); // Cierra el modal actual
    openPacienteModal(); // Abre el nuevo modal para el paciente seleccionado
  };

  const openPacienteModal = () => {
    setModalOpen(true);

    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  function convertirFechaLarga(fechaStr) {
    const fecha = new Date(fechaStr);
    if (isNaN(fecha.getTime())) {
      console.error("Fecha inválida para convertir.");
      return "";
    }

    const opcionesFecha = { day: "numeric", month: "long", year: "numeric" };
    const opcionesHora = { hour: "numeric", minute: "numeric", hour12: true };

    // Obtener partes de la fecha
    const fechaLarga = new Intl.DateTimeFormat("es-ES", opcionesFecha).format(
      fecha
    );
    const horaLarga = new Intl.DateTimeFormat("es-ES", opcionesHora).format(
      fecha
    );

    const partesFecha = fechaLarga.split(" de ");
    const dia = partesFecha[0];
    const mes = partesFecha[1];
    const año = partesFecha[2];

    return `día ${dia} del mes de ${mes} del año ${año} a las ${horaLarga}`;
  }
  const fechaStr = "6/6/2024 9:44 AM";
  const fechaLarga = convertirFechaLarga(fechaStr);

  const convertirFechaActualLarga = () => {
    const fecha = new Date();
    if (isNaN(fecha.getTime())) {
      console.error("Fecha inválida para convertir.");
      return "";
    }

    const opcionesFecha = { day: "numeric", month: "long", year: "numeric" };
    const opcionesHora = { hour: "numeric", minute: "numeric", hour12: true };

    // Obtener partes de la fecha
    const fechaLarga = new Intl.DateTimeFormat("es-ES", opcionesFecha).format(
      fecha
    );
    const horaLarga = new Intl.DateTimeFormat("es-ES", opcionesHora).format(
      fecha
    );

    const partesFecha = fechaLarga.split(" de ");
    const dia = partesFecha[0];
    const mes = partesFecha[1];
    const año = partesFecha[2];

    return `día ${dia} del mes de ${mes} del año ${año} a las ${horaLarga}`;
  };

  const isHtmlContent = (value = "") => {
    if (!value || typeof value !== "string") {
      return false;
    }

    return /<\/?[a-z][\s\S]*>/i.test(value);
  };


  const renderPlainTextToPDF = (
    doc,
    text = "",
    startY,
    pageWidth,
    pageHeight,
    margin = 10
  ) => {
    let y = startY;

    const lineHeight = 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);

    const paragraphs = String(text || "").split(/\r?\n/);

    paragraphs.forEach((paragraph) => {

      if (!paragraph.trim()) {
        y += lineHeight;
        return;
      }

      const lines = doc.splitTextToSize(
        paragraph,
        pageWidth - margin * 2
      );

      lines.forEach((line) => {

        if (y + lineHeight > pageHeight - 20) {
          doc.addPage();
          y = 20;
        }

        doc.text(
          line,
          margin,
          y
        );

        y += lineHeight;

      });

    });

    return y;
  };


  const renderRichTextToPDF = (
    doc,
    html,
    startY,
    pageWidth,
    pageHeight,
    margin = 10
    ) => {
      const parser = new DOMParser();

      const htmlDoc = parser.parseFromString(
        html || "",
        "text/html"
      );

      let y = startY;

      const fontSize = 12;
      const lineHeight = 7;

      const checkPage = () => {
        if (y + lineHeight > pageHeight - 20) {
          doc.addPage();
          y = 20;
        }
      };


      const renderInline = (
        node,
        x,
        currentY,
        styles = {}
      ) => {

        if (node.nodeType === Node.TEXT_NODE) {

          const text = node.textContent || "";

          if (!text.trim()) {
            return x;
          }

          let fontStyle = "normal";

          if (styles.bold && styles.italic) {
            fontStyle = "bolditalic";
          } else if (styles.bold) {
            fontStyle = "bold";
          } else if (styles.italic) {
            fontStyle = "italic";
          }

          doc.setFont("helvetica", fontStyle);
          doc.setFontSize(fontSize);

          const words = text.split(/\s+/);

          words.forEach((word, index) => {

            if (!word) return;

            const value =
              index < words.length - 1
                ? `${word} `
                : word;

            const wordWidth = doc.getTextWidth(value);

            if (x + wordWidth > pageWidth - margin) {
              y += lineHeight;
              currentY = y;
              x = margin;

              checkPage();
            }

            doc.text(
              value,
              x,
              currentY
            );

            if (styles.underline) {

              const underlineY =
                currentY + 1;

              doc.setLineWidth(0.2);

              doc.line(
                x,
                underlineY,
                x + wordWidth,
                underlineY
              );

            }

            x += wordWidth;

          });

          return x;
        }


        if (node.nodeType !== Node.ELEMENT_NODE) {
          return x;
        }


        const tag =
          node.tagName.toLowerCase();


        const childStyles = {

          ...styles,

          bold:
            styles.bold ||
            tag === "strong" ||
            tag === "b",

          italic:
            styles.italic ||
            tag === "em" ||
            tag === "i",

          underline:
            styles.underline ||
            tag === "u",

        };


        node.childNodes.forEach((child) => {

          x = renderInline(
            child,
            x,
            currentY,
            childStyles
          );

        });


        return x;
      };


      const renderParagraph = (element) => {

        checkPage();

        const align =
          element.style.textAlign || "left";

        const text =
          element.textContent?.trim() || "";

        if (!text) {
          y += lineHeight;
          return;
        }


        if (
          align === "center" ||
          align === "right"
        ) {

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(fontSize);

          const lines =
            doc.splitTextToSize(
              text,
              pageWidth - margin * 2
            );

          lines.forEach((line) => {

            checkPage();

            if (align === "center") {

              doc.text(
                line,
                pageWidth / 2,
                y,
                {
                  align: "center",
                }
              );

            } else {

              doc.text(
                line,
                pageWidth - margin,
                y,
                {
                  align: "right",
                }
              );

            }

            y += lineHeight;

          });

        } else {

          let x = margin;

          renderInline(
            element,
            x,
            y
          );

          y += lineHeight;

        }


        y += 2;
      };


      const renderList = (
        element,
        level = 0
      ) => {

        const tag =
          element.tagName.toLowerCase();

        const items =
          Array.from(element.children)
            .filter((child) =>
              child.tagName.toLowerCase() === "li"
            );


        items.forEach((li, index) => {

          checkPage();

          const indent =
            margin + level * 8;

          const bullet =
            tag === "ol"
              ? `${index + 1}.`
              : "•";

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(fontSize);

          doc.text(
            bullet,
            indent,
            y
          );

          const textStart =
            indent + 6;


          const directNodes =
            Array.from(li.childNodes)
              .filter((node) => {

                if (
                  node.nodeType === Node.ELEMENT_NODE &&
                  ["UL", "OL"].includes(
                    node.tagName
                  )
                ) {
                  return false;
                }

                return true;
              });


          let x = textStart;

          directNodes.forEach((node) => {

            x = renderInline(
              node,
              x,
              y
            );

          });


          y += lineHeight;


          Array.from(li.children)
            .filter((child) =>
              ["UL", "OL"].includes(
                child.tagName
              )
            )
            .forEach((nestedList) => {

              renderList(
                nestedList,
                level + 1
              );

            });

        });


        y += 2;
      };


      const renderBlock = (
        element,
        level = 0
      ) => {

        const tag =
          element.tagName?.toLowerCase();

        if (!tag) {
          return;
        }


        if (tag === "p") {

          renderParagraph(element);

        } else if (
          tag === "ul" ||
          tag === "ol"
        ) {

          renderList(
            element,
            level
          );

        } else {

          Array.from(
            element.children
          ).forEach((child) => {

            renderBlock(
              child,
              level
            );

          });

        }

      };


      Array.from(
        htmlDoc.body.children
      ).forEach((element) => {

        renderBlock(element);

      });


      return y;
    };


  const renderCertificateContent = (
    doc,
    content,
    startY,
    pageWidth,
    pageHeight,
    margin = 10
  ) => {

    if (!content) {
      return startY;
    }


    if (isHtmlContent(content)) {

      return renderRichTextToPDF(
        doc,
        content,
        startY,
        pageWidth,
        pageHeight,
        margin
      );

    }


    return renderPlainTextToPDF(
      doc,
      content,
      startY,
      pageWidth,
      pageHeight,
      margin
    );

  };


  const previsualizarPDF = async (certMedico) => {

    const doc = new jsPDF();
    // ============================================
    // CONFIGURACION
    // ============================================

    const pageWidth =
      doc.internal.pageSize.getWidth();

    const pageHeight =
      doc.internal.pageSize.getHeight();

    const margin = 10;

    const additionalMargin = 40;

    const fontSize = 10;

    const lineHeight =
      fontSize * 0.6;


    doc.setFontSize(fontSize);


    const fechaLarga =
      convertirFechaActualLarga();


    // ============================================
    // TITULO
    // ============================================

    doc.setFontSize(22);

    doc.setFont(
      "helvetica",
      "bold"
    );


    const detalleText =
      "CERTIFICADO MEDICO";


    const textWidth =
      doc.getTextWidth(detalleText);


    const centeredX =
      (pageWidth - textWidth) / 2;


    doc.text(
      detalleText,
      centeredX,
      margin + additionalMargin
    );


    doc.setFont(
      "helvetica",
      "normal"
    );


    doc.setFontSize(12);


    // ============================================
    // INFORMACION DOCTOR / PACIENTE
    // ============================================

    const profileText =
      `Yo, ${profileData.docNom}, Médico ${
        profileData.docEsp
      }, Cédula de identidad número. ${
        profileData.docCed
      }, provisto del correspondiente exequátur No. ${
        profileData.docExe
      }, certifico haber examinado a ${
        selectedPaciente?.pcteNom || ""
      } ${
        selectedPaciente?.pcteNom2 || ""
      } ${
        selectedPaciente?.pcteApe1 || ""
      } ${
        selectedPaciente?.pcteApe2 || ""
      } Cédula de identidad número. ${
        selectedPaciente?.pcteNumDoc || ""
      }`.trim();


    const profileLines =
      doc.splitTextToSize(
        profileText,
        pageWidth - 2 * margin
      );


    let y =
      margin +
      additionalMargin +
      20;


    profileLines.forEach((line) => {

      if (
        y + lineHeight >
        pageHeight -
          margin -
          additionalMargin -
          lineHeight
      ) {

        doc.addPage();

        y =
          margin +
          additionalMargin;

      }


      doc.text(
        line,
        margin,
        y
      );


      y += lineHeight;

    });


    // ============================================
    // CERTIFICO
    // ============================================

    y += 10;


    if (
      y + lineHeight >
      pageHeight - 20
    ) {

      doc.addPage();

      y = 20;

    }


    doc.setFont(
      "helvetica",
      "bold"
    );


    doc.setFontSize(12);


    doc.text(
      "Certifico:",
      margin,
      y
    );


    y += 8;


    y = renderCertificateContent(
      doc,
      formulario.constatado,
      y,
      pageWidth,
      pageHeight,
      margin
    );


    // ============================================
    // RECOMENDACION
    // ============================================

    y += 8;


    if (
      y + lineHeight >
      pageHeight - 20
    ) {

      doc.addPage();

      y = 20;

    }


    doc.setFont(
      "helvetica",
      "bold"
    );


    doc.setFontSize(12);


    doc.text(
      "Por lo que recomiendo:",
      margin,
      y
    );


    y += 8;


    y = renderCertificateContent(
      doc,
      formulario.recomedacion,
      y,
      pageWidth,
      pageHeight,
      margin
    );


    doc.setFont(
      "helvetica",
      "normal"
    );


    // ============================================
    // FOOTER
    // ============================================

    const footerText =
      `${profileData.docDirCert} a petición del interesado, hoy ${fechaLarga}`.trim();


    // y += lineHeight + 2;


    const footerLines =
      doc.splitTextToSize(
        footerText,
        pageWidth - 2 * margin
      );


    if (
      y +
        footerLines.length *
          lineHeight >
      pageHeight -
        margin -
        additionalMargin
    ) {

      doc.addPage();

      y =
        margin +
        additionalMargin;

    }


    footerLines.forEach((line) => {

      doc.text(
        line,
        margin,
        y
      );

      y += lineHeight;

    });


    // ============================================
    // FIRMA / NOMBRE DOCTOR
    // ============================================

    let alturaNombre =
      y + 70;


    // Si no cabe la firma, crear nueva página
    if (
      alturaNombre + 15 >
      pageHeight - margin
    ) {

      doc.addPage();

      y = 40;

      alturaNombre =
        y + 70;

    }


    const lineaY =
      alturaNombre - 5;


    const lineaX =
      (pageWidth - 80) / 2;


    doc.setLineWidth(0.5);


    doc.line(
      lineaX,
      lineaY,
      lineaX + 80,
      lineaY
    );


    const nombreDoctor =
      `${profileData.docNom || ""}`;


    doc.setFontSize(12);


    doc.setFont(
      "helvetica",
      "normal"
    );


    doc.text(
      nombreDoctor,
      pageWidth / 2,
      alturaNombre,
      {
        align: "center",
      }
    );


    const exequaturText =
      `${profileData.docExe || ""}`;


    doc.text(
      exequaturText,
      pageWidth / 2,
      alturaNombre + 5,
      {
        align: "center",
      }
    );


    // ============================================
    // GENERAR PDF
    // ============================================

    const pdfBlob =
      doc.output("blob");


    const iframe =
      document.createElement(
        "iframe"
      );


    iframe.style.display =
      "none";


    document.body.appendChild(
      iframe
    );


    const blobUrl =
      URL.createObjectURL(
        pdfBlob
      );


    iframe.src = blobUrl;


    iframe.onload = () => {

      iframe.contentWindow.focus();

      iframe.contentWindow.print();


      iframe.contentWindow.onafterprint =
        () => {

          document.body.removeChild(
            iframe
          );

          URL.revokeObjectURL(
            blobUrl
          );

        };

    };

  };

  // Contador de caractereres
  const handleChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxChars1) {
      setConstatado(text);
      setRecomedacion(text);
    }
  };

  const sortData = (column) => {
    const isAscending = sortColumn === column && sortDirection === "asc";

    const sortFunction = (a, b) => {
      let aValue = a[column];
      let bValue = b[column];

      if (column === "id") {
        // Ordenar numéricamente
        return isAscending ? aValue - bValue : bValue - aValue;
      } else if (column === "fechaCertificado") {
        // Asegúrate de que aValue y bValue sean fechas válidas
        return isAscending
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      } else {
        // Comparar cadenas
        aValue = String(aValue || ""); // Convertir a cadena o usar una cadena vacía
        bValue = String(bValue || ""); // Convertir a cadena o usar una cadena vacía

        return isAscending
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
    };

    // Ordena los datos
    const sortedData = [...certMedicos].sort(sortFunction);

    setCertMedicos(sortedData); // Actualiza el estado con los datos ordenados
    setSortColumn(column);
    setSortDirection(isAscending ? "desc" : "asc");
  };

  const SkeletonTableRow = () => (
    <tr>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "150px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "70px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "200px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "100px" }}
        ></div>
      </td>
      <td>
        <div
          className="skeleton-placeholder"
          style={{ backgroundColor: "#e0e0e0", height: "20px", width: "60px" }}
        ></div>
      </td>
    </tr>
  );

  const getPlainText = (html = "") => {
    if (!html) return "";

    const div = document.createElement("div");
    div.innerHTML = html;

    return (div.textContent || div.innerText || "")
      .replace(/\s+/g, " ")
      .trim();
  };

  const truncatePlainText = (html = "", maxLength = 45) => {
    const text = getPlainText(html);

    return text.length > maxLength
      ? `${text.slice(0, maxLength)}...`
      : text;
  };
  const getPlainTextLength = (html = "") => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return (div.textContent || div.innerText || "").length;
  };

  // Guardar formato de TipTap (mayusculas, cursivas, etc.)
  const RichTextDisplay = ({ content }) => {
    if (!content) {
      return (
        <span className="text-muted">
          Sin información
        </span>
      );
    }

    return (
      <div
        className="rich-text-display"
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />
    );
  };

  return (
  <div className="pro-container">

    {/* ===================================================== */}
    {/* HEADER */}
    {/* ===================================================== */}

    <div className="pro-header">

      <div className="pro-header-left">

        <h2 className="pro-title">
          Certificados Médicos
        </h2>

        <p className="pro-subtitle">
          Gestión y emisión de certificados médicos
        </p>

      </div>

      <div className="pro-actions">

        <div className="pro-search">

          <i className="bi bi-search"></i>

          <input
            type="search"
            placeholder="Buscar certificado..."
            value={searchTerm}
            onChange={handleSearchTermChange}
          />

        </div>

        <button
          type="button"
          className="pro-btn-primary"
          onClick={() => openModal(null, 10)}
        >

          <i className="bi bi-plus-circle"></i>
          Nuevo Certificado

        </button>

      </div>

    </div>

    {/* ===================================================== */}
    {/* TABLE DESKTOP */}
    {/* ===================================================== */}

    <div className="pro-card d-none d-md-block">

      <div className="table-responsive">

        <table className="table pro-table">

          <thead>

            <tr>

              <th onClick={() => sortData("certifiId")}>
                ID
              </th>

              <th onClick={() => sortData("nombre")}>
                Paciente
              </th>

              <th onClick={() => sortData("direccion")}>
                Dirección
              </th>

              <th onClick={() => sortData("telefono")}>
                Teléfono
              </th>

              <th onClick={() => sortData("fechaCertificado")}>
                Fecha
              </th>

              <th>
                Acciones
              </th>

            </tr>

          </thead>

          <tbody>

            {loading ? (

              Array.from({ length: 13 }).map((_, index) => (
                <SkeletonTableRow key={index} />
              ))

            ) : (

              displayedcertMedicos.map((certMedico) => (

                <tr key={certMedico.certifiId}>

                  <td>
                    #{certMedico.certifiId}
                  </td>

                  <td
                    className="fw-semibold"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={certMedico.fullName}
                  >

                    {certMedico.fullName?.length > 35
                      ? `${certMedico.fullName.slice(0, 35)}...`
                      : certMedico.fullName}

                  </td>

                  <td
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={certMedico.pcteDir}
                  >

                    {certMedico.pcteDir?.length > 40
                      ? `${certMedico.pcteDir.slice(0, 40)}...`
                      : certMedico.pcteDir}

                  </td>

                  <td>
                    {certMedico.pcteCel}
                  </td>

                  <td>
                    {certMedico.certifi_fe}
                  </td>

                  <td>

                    <div className="pro-table-actions">

                      <button
                        type="button"
                        className="pro-action-btn primary"
                        title="Editar"
                        onClick={() => openModal(certMedico)}
                      >

                        <i className="bi bi-pencil-square"></i>

                      </button>

                    </div>

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

    </div>

    {/* ===================================================== */}
    {/* MOBILE CARDS */}
    {/* ===================================================== */}

    <div className="d-md-none">

      <div className="row">

        {loading ? (

          Array.from({ length: 5 }).map((_, index) => (

            <div className="col-12 mb-3" key={index}>

              <div className="mobile-patient-card">

                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line mb-3"></div>
                <div className="skeleton-line"></div>

              </div>

            </div>

          ))

        ) : (

          displayedcertMedicos.map((certMedico) => (

            <div
              className="col-12 mb-3"
              key={certMedico.certifiId}
            >

              <div className="mobile-patient-card">

                <div className="mobile-card-top">

                  <div className="patient-info">

                    <div className="patient-avatar">

                      {certMedico.fullName?.charAt(0)}

                    </div>

                    <div>

                      <div className="patient-name">

                        {certMedico.fullName}

                      </div>

                      <div className="patient-id">

                        Certificado #{certMedico.certifiId}

                      </div>

                    </div>

                  </div>

                </div>

                <div className="mb-3">

                  <small className="text-muted d-block mb-1">
                    Dirección
                  </small>

                  <div>
                    {certMedico.pcteDir}
                  </div>

                </div>

                <div className="mb-3">

                  <small className="text-muted d-block mb-1">
                    Teléfono
                  </small>

                  <div>
                    {certMedico.pcteCel}
                  </div>

                </div>

                <div className="mb-3">

                  <small className="text-muted d-block mb-1">
                    Fecha
                  </small>

                  <div>
                    {certMedico.certifi_fe}
                  </div>

                </div>

                <div className="mobile-card-actions">

                  <button
                    type="button"
                    className="pro-action-btn primary"
                    onClick={() => openModal(certMedico)}
                  >

                    <i className="bi bi-pencil-square"></i>

                  </button>

                </div>

              </div>

            </div>

          ))

        )}

      </div>

    </div>

    {/* ===================================================== */}
    {/* PAGINATION */}
    {/* ===================================================== */}

    {!showForm && (

      <div className="pro-pagination">

        <nav aria-label="Page navigation example">

          <ul className="pagination modern-pagination">

            <li
              className={`page-item ${
                pageNumber === 0 ? "disabled" : ""
              }`}
            >

              <span
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber - 1,
                  })
                }
              >

                <i className="bi bi-chevron-left"></i>

              </span>

            </li>

            {Array.from(
              { length: Math.min(pageCount, 10) },
              (_, index) => (

                <li
                  key={index}
                  className={`page-item ${
                    pageNumber === index
                      ? "active"
                      : ""
                  }`}
                >

                  <span
                    className="page-link"
                    onClick={() =>
                      handlePageChange({
                        selected: index,
                      })
                    }
                  >

                    {index + 1}

                  </span>

                </li>

              )
            )}

            <li
              className={`page-item ${
                pageNumber === pageCount - 1
                  ? "disabled"
                  : ""
              }`}
            >

              <span
                className="page-link"
                onClick={() =>
                  handlePageChange({
                    selected: pageNumber + 1,
                  })
                }
              >

                <i className="bi bi-chevron-right"></i>

              </span>

            </li>

          </ul>

        </nav>

      </div>

    )}

    {/* ===================================================== */}
    {/* MODAL */}
    {/* ===================================================== */}

    <Modal
      isOpen={modalOpen}
      toggle={closeModal}
      centered
      modalClassName="factura-modal"
    >

      <ModalHeader toggle={closeModal}>

        {isEditing
          ? "Editar Certificado Médico"
          : "Nuevo Certificado Médico"}

      </ModalHeader>

      <ModalBody>

        {selectedPaciente ? (

          <div className="row g-4">

            {/* LEFT */}
            <div className="col-12 col-xl-4">

              <div className="pro-form-card">

                <div className="pro-patient-profile">

                  <div className="pro-patient-avatar">

                    {selectedPaciente.pcteNom?.charAt(0)}

                  </div>

                  <div>

                    <div className="pro-patient-name">

                      {selectedPaciente.pcteNom}
                      {" "}
                      {selectedPaciente.pcteApe1}

                    </div>

                    <div className="pro-patient-meta">

                      {selectedPaciente.pcteEd} años
                      •
                      {selectedPaciente.pcteSx}

                    </div>

                  </div>

                </div>

                <div className="mt-4">

                  <label className="pro-label">
                    Fecha
                  </label>

                  <Input
                    className="pro-input"
                    value={formulario.fechaCertMedico}
                    disabled
                  />

                </div>

                <div className="mt-3">

                  <label className="pro-label">
                    Dirección
                  </label>

                  <Input
                    type="textarea"
                    className="pro-input"
                    value={profileData.docDirCert}
                    disabled
                  />

                </div>

              </div>

            </div>

            {/* RIGHT */}
            <div className="col-12 col-xl-8">

              <div className="pro-form-card">

                <FormGroup className="mb-4">

                  <Label className="pro-label">
                    Constatado
                  </Label>

                  <div
                    className="position-relative"

                    onBeforeInputCapture={(e) => {
                      const currentLength =
                        getPlainTextLength(formulario.constatado || "");

                      const newText = e.data || "";

                      if (currentLength >= maxChars1) {
                        e.preventDefault();
                        return;
                      }

                      if (
                        currentLength + newText.length >
                        maxChars1
                      ) {
                        e.preventDefault();
                      }
                    }}

                    onPasteCapture={(e) => {
                      const currentLength =
                        getPlainTextLength(formulario.constatado || "");

                      const pastedText =
                        e.clipboardData?.getData("text") || "";

                      if (
                        currentLength + pastedText.length >
                        maxChars1
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >

                    <ProRichTextEditor
                      value={formulario.constatado || ""}
                      onChange={(html) => {
                        const plainTextLength =
                          getPlainTextLength(html);

                        if (plainTextLength <= maxChars1) {
                          setFormulario((prev) => ({
                            ...prev,
                            constatado: html,
                          }));
                        }
                      }}
                      placeholder="Escriba la información constatada..."
                    />

                    <div className="pro-rich-counter">
                      {Math.max(
                        0,
                        maxChars1 -
                          getPlainTextLength(formulario.constatado || "")
                      )}
                      /{maxChars1}
                    </div>

                  </div>

                </FormGroup>


                <FormGroup className="mb-4">

                  <Label className="pro-label">
                    Recomendación
                  </Label>

                  <div
                    className="position-relative"

                    onBeforeInputCapture={(e) => {
                      const currentLength =
                        getPlainTextLength(formulario.recomedacion || "");

                      const newText = e.data || "";

                      if (currentLength >= maxChars1) {
                        e.preventDefault();
                        return;
                      }

                      if (
                        currentLength + newText.length >
                        maxChars1
                      ) {
                        e.preventDefault();
                      }
                    }}

                    onPasteCapture={(e) => {
                      const currentLength =
                        getPlainTextLength(formulario.recomedacion || "");

                      const pastedText =
                        e.clipboardData?.getData("text") || "";

                      if (
                        currentLength + pastedText.length >
                        maxChars1
                      ) {
                        e.preventDefault();
                      }
                    }}
                  >

                    <ProRichTextEditor
                      value={formulario.recomedacion || ""}
                      onChange={(html) => {
                        const plainTextLength =
                          getPlainTextLength(html);

                        if (plainTextLength <= maxChars1) {
                          setFormulario((prev) => ({
                            ...prev,
                            recomedacion: html,
                          }));
                        }
                      }}
                      placeholder="Escriba las recomendaciones médicas..."
                    />

                    <div className="pro-rich-counter">
                      {Math.max(
                        0,
                        maxChars1 -
                          getPlainTextLength(formulario.recomedacion || "")
                      )}
                      /{maxChars1}
                    </div>

                  </div>

                </FormGroup>

              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="pro-search-modal mb-4">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Buscar paciente..."
                value={modalSearchTerm}
                onChange={handleModalSearchTermChange}
                ref={searchInputRef}
              />
            </div>
            <div className="pro-patient-list">
              {filteredPacientes
                .slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                )
                .map((paciente) => {

                  const fullName = [
                    paciente.pcteNom,
                    paciente.pcteNom2,
                    paciente.pcteApe1,
                    paciente.pcteApe2,
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (

                    <div
                      key={paciente.pcteId}
                      className="pro-patient-item"
                    >

                      <div className="patient-info">

                        <div className="patient-avatar">

                          {paciente.pcteNom?.charAt(0)}

                        </div>

                        <div>

                          <div className="patient-name">
                            {fullName}
                          </div>

                          <div className="patient-id">
                            #{paciente.pcteId}
                          </div>

                        </div>

                      </div>

                      <button
                        className="pro-btn-primary-sm"
                        onClick={() =>
                          handlePacienteSelection(paciente)
                        }
                      >

                        Seleccionar

                      </button>

                    </div>

                  );

                })}

            </div>

          </div>

        )}

      </ModalBody>

      <ModalFooter>

        {selectedPaciente ? (

          <>

            <Button
              className="pro-btn-cancel"
              onClick={closeModal}
            >

              Cancelar

            </Button>

            <Button
              className="pro-btn-save"
              onClick={
                isEditing
                  ? editCertMedico
                  : guardarCertMedico
              }
            >

              {isEditing
                ? "Guardar Edición"
                : "Guardar"}

            </Button>

            <Button
              className="pro-btn-print"
              onClick={() =>
                previsualizarPDF(selectedCertMedicosLocal)
              }
            >

              <i className="bi bi-printer-fill"></i>

              Imprimir

            </Button>

          </>

        ) : (

          <Button
            className="pro-btn-cancel"
            onClick={closeModal}
          >

            Cancelar

          </Button>

        )}

      </ModalFooter>

    </Modal>

  </div>
);
};

export default CertMedicoList;
